import type { IntRange, Sum, UnionToTuple } from 'type-fest'
import { z } from 'zod'

import type {
  MaxValue,
  ObjectValuesToTuple,
  SumValues,
  ZodSimplify,
} from '@mntn-dev/utility-types'

/**
 * Schemas and types for Maker Application (bid) Rating
 *
 * This module implements a comprehensive scoring system for evaluating maker bids
 * based on various criteria including experience, quality, certifications, and
 * project alignment. The scoring system helps brands identify the best makers
 * for their projects.
 *
 * Reference: https://docs.google.com/spreadsheets/d/1qJsJpgjszt6gyxbX5RG-gTm2AkyOStWkVj4CkxIeUiM/edit?gid=0#gid=0
 */

/**
 * Rating Category Score (Experience/Quality)
 *
 * Converts numerical ratings (0-5 scale) into discrete scoring categories:
 *  3: Excellent (4.9+) - Top tier makers with outstanding ratings
 *  2: Good (4.5 - 4.89) - High quality makers with strong track records
 *  1: Average (4 - 4.49) - Decent makers with acceptable performance
 *  0: No rating - New makers or those without sufficient feedback
 * -1: Poor (Below 4) - Makers with concerning performance history
 */
export const RatingCategoryScoreSchema = z.union([
  z.literal(3),
  z.literal(2),
  z.literal(1),
  z.literal(0),
  z.literal(-1),
])

export type RatingCategoryScore = ZodSimplify<typeof RatingCategoryScoreSchema>

// Input schema for raw rating values (0-5 scale from user feedback)
export const RatingCategoryScoreInputSchema = z.number().min(0).max(5)

export type RatingCategoryScoreInput = ZodSimplify<
  typeof RatingCategoryScoreInputSchema
>

/**
 * Converts a raw rating (0-5 scale) to a categorical score
 *
 * @param input - Raw rating value from user feedback (default: 0)
 * @returns Categorical score based on rating thresholds
 */
export const toRatingCategoryScore = (input: RatingCategoryScoreInput = 0) =>
  RatingCategoryScoreSchema.parse(
    [
      { min: 4.9, score: 3 },
      { min: 4.5, score: 2 },
      { min: 4.0, score: 1 },
    ].find((threshold) => input >= threshold.min)?.score ?? -1
  )

/**
 * Count Category Score (Projects Rated/Projects Completed)
 *
 * Evaluates maker experience based on project volume:
 *  2: Experienced (3+ projects) - Proven track record with multiple projects
 *  1: Some experience (1-2 projects) - Limited but existing experience
 *  0: No experience (0 projects) - New to the platform or category
 */
export const CountCategoryScoreSchema = z.union([
  z.literal(2),
  z.literal(1),
  z.literal(0),
])

export type CountCategoryScore = ZodSimplify<typeof CountCategoryScoreSchema>

// Input schema for project count values
export const CountCategoryScoreInputSchema = z.number().nonnegative()

export type CountCategoryScoreInput = ZodSimplify<
  typeof CountCategoryScoreInputSchema
>

/**
 * Converts a project count to a categorical score
 *
 * @param input - Number of projects (rated or completed)
 * @returns Categorical score based on experience thresholds
 */
export const toCountCategoryScore = (
  input: CountCategoryScoreInput = 0
): CountCategoryScore =>
  CountCategoryScoreSchema.parse(
    [
      { min: 3, score: 2 },
      { min: 1, score: 1 },
    ].find((threshold) => input >= threshold.min)?.score ?? 0
  )

/**
 * High Value Category Score
 *
 * Binary scoring for high-impact criteria (worth 3 points):
 *  3: Criteria met - Significant positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: budget alignment, location match, tier 1 status, certifications, skill alignment, language match
 */
export const HighValueCategoryScoreSchema = z.union([
  z.literal(3),
  z.literal(0),
])

export type HighValueCategoryScore = ZodSimplify<
  typeof HighValueCategoryScoreSchema
>

// Input schema for boolean criteria
export const HighValueCategoryScoreInputSchema = z.boolean()

export type HighValueCategoryScoreInput = ZodSimplify<
  typeof HighValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a high-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 3 points if true, 0 if false
 */
export const toHighValueCategoryScore = (
  input: HighValueCategoryScoreInput = false
) => HighValueCategoryScoreSchema.parse(input ? 3 : 0)

/**
 * Medium Value Category Score
 *
 * Binary scoring for moderate-impact criteria (worth 2 points):
 *  2: Criteria met - Moderate positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: platform tag alignment, relevant portfolio examples
 */
export const MediumValueCategoryScoreSchema = z.union([
  z.literal(2),
  z.literal(0),
])

export type MediumValueCategoryScore = ZodSimplify<
  typeof MediumValueCategoryScoreSchema
>

// Input schema for boolean criteria
export const MediumValueCategoryScoreInputSchema = z.boolean()

export type MediumValueCategoryScoreInput = ZodSimplify<
  typeof MediumValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a medium-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 2 points if true, 0 if false
 */
export const toMediumValueCategoryScore = (
  input: MediumValueCategoryScoreInput = false
) => MediumValueCategoryScoreSchema.parse(input ? 2 : 0)

/**
 * Low Value Category Score
 *
 * Binary scoring for low-impact criteria (worth 1 point):
 *  1: Criteria met - Minor positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: industry tag alignment, similar past budget experience
 */
export const LowValueCategoryScoreSchema = z.union([z.literal(1), z.literal(0)])

export type LowValueCategoryScore = ZodSimplify<
  typeof LowValueCategoryScoreSchema
>

// Input schema for boolean criteria
export const LowValueCategoryScoreInputSchema = z.boolean()

export type LowValueCategoryScoreInput = ZodSimplify<
  typeof LowValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a low-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 1 point if true, 0 if false
 */
export const toLowValueCategoryScore = (
  input: LowValueCategoryScoreInput = false
) => LowValueCategoryScoreSchema.parse(input ? 1 : 0)

/**
 * Bid Scorecard Model
 *
 * Complete scoring model for evaluating maker bids across all criteria:
 *
 * Performance Metrics:
 * - experienceRating: Overall experience rating from past projects
 * - qualityRating: Quality rating from past work
 * - projectsRated: Number of projects that have been rated
 * - projectsCompleted: Total number of completed projects
 *
 * Project Alignment:
 * - inBudget: Whether maker's bid is within the project budget
 * - inLocation: Whether maker is in the preferred location/timezone
 * - meetsIndustryTags: Alignment with industry/vertical requirements
 * - meetsPlatformTags: Experience with required platforms (TikTok, Meta, etc.)
 * - meetsSkillTags: Possession of required skills
 * - meetsLanguageTags: Language requirements met
 * - similarPastBudget: Experience with similar budget ranges
 * - relevantExamples: Portfolio contains relevant work examples
 *
 * Certifications & Status:
 * - tier1Status: Premium tier maker status
 * - generalQfCertified: General platform certification
 * - mntnCertified: MNTN-specific certification
 * - tikTokCertified: TikTok platform certification
 * - metaCertified: Meta/Facebook platform certification
 */
export const ScoredBidModelSchema = z.object({
  experienceRating: RatingCategoryScoreSchema,
  qualityRating: RatingCategoryScoreSchema,
  projectsRated: CountCategoryScoreSchema,
  projectsCompleted: CountCategoryScoreSchema,
  inBudget: HighValueCategoryScoreSchema,
  inLocation: HighValueCategoryScoreSchema,
  tier1Status: HighValueCategoryScoreSchema,
  generalQfCertified: HighValueCategoryScoreSchema,
  mntnCertified: HighValueCategoryScoreSchema,
  tikTokCertified: HighValueCategoryScoreSchema,
  metaCertified: HighValueCategoryScoreSchema,
  meetsIndustryTags: LowValueCategoryScoreSchema,
  meetsPlatformTags: MediumValueCategoryScoreSchema,
  meetsSkillTags: HighValueCategoryScoreSchema,
  similarPastBudget: LowValueCategoryScoreSchema,
  meetsLanguageTags: HighValueCategoryScoreSchema,
  relevantExamples: MediumValueCategoryScoreSchema,
})

export type ScoredBidModel = ZodSimplify<typeof ScoredBidModelSchema>

/**
 * Maximum possible bid score
 *
 * Calculated by summing the maximum values from each scoring category.
 * This represents a perfect score where a maker meets all criteria
 * at the highest level.
 */
export type MaxBidScore = SumValues<
  ObjectValuesToTuple<{
    [K in keyof ScoredBidModel]: MaxValue<
      {
        [K in keyof ScoredBidModel]: UnionToTuple<ScoredBidModel[K]>
      }[K]
    >
  }>
>

// Calculate the actual maximum score value at compile time
export const MaxBidScore = ScoredBidModelSchema.keyof().options.reduce<number>(
  (score, category) =>
    score +
    Math.max(
      ...ScoredBidModelSchema.shape[category].options.map(
        (option) => option.value
      )
    ),
  0
) as MaxBidScore

/**
 * Bid Score
 *
 * Final numerical score for a bid, constrained to be between 0 and MaxBidScore.
 * Higher scores indicate better alignment with project requirements and
 * stronger maker credentials.
 */
export const BidScoreSchema = z
  .number()
  .int()
  .nonnegative()
  .refine(
    // If this numeric range type proves too difficult to work with, we can just use
    // .max(MaxBidScore) instead and have the bid score just be a nonnegative number.
    (score): score is IntRange<0, Sum<1, MaxBidScore>> => score <= MaxBidScore
  )

export type BidScore = ZodSimplify<typeof BidScoreSchema>

/**
 * Bid Scorecard Input Model
 *
 * Input schema for raw data used to calculate bid scores.
 * All fields are optional to handle cases where data may be incomplete.
 * Uses raw input types (numbers, booleans) that get converted to
 * categorical scores during evaluation.
 */
export const BidScorecardModelSchema = z
  .object({
    // Rating Categories
    experienceRating: RatingCategoryScoreInputSchema,
    qualityRating: RatingCategoryScoreInputSchema,
    // Count Categories
    projectsRated: CountCategoryScoreInputSchema,
    projectsCompleted: CountCategoryScoreInputSchema,
    // High Value Categories
    inBudget: HighValueCategoryScoreInputSchema,
    inLocation: HighValueCategoryScoreInputSchema,
    tier1Status: HighValueCategoryScoreInputSchema,
    generalQfCertified: HighValueCategoryScoreInputSchema,
    mntnCertified: HighValueCategoryScoreInputSchema,
    tikTokCertified: HighValueCategoryScoreInputSchema,
    metaCertified: HighValueCategoryScoreInputSchema,
    meetsSkillTags: HighValueCategoryScoreInputSchema,
    meetsLanguageTags: HighValueCategoryScoreInputSchema,
    // Medium Value Categories
    meetsPlatformTags: MediumValueCategoryScoreInputSchema,
    relevantExamples: MediumValueCategoryScoreInputSchema,
    // Low Value Categories
    meetsIndustryTags: LowValueCategoryScoreInputSchema,
    similarPastBudget: LowValueCategoryScoreInputSchema,
  })
  .partial()

export type BidScorecardModel = ZodSimplify<typeof BidScorecardModelSchema>

/**
 * Evaluates a bid scorecard and returns the total score
 *
 * Takes raw input data and converts each field to its appropriate
 * categorical score, then sums all scores to get the final bid score.
 * Missing fields default to 0 points.
 *
 * @param scorecard - Raw scorecard data with optional fields
 * @returns Total bid score (0 to MaxBidScore)
 *
 * @example
 * ```typescript
 * const score = calculateBidScore({
 *   experienceRating: 4.8,
 *   inBudget: true,
 *   meetsSkillTags: true,
 *   // ... other criteria
 * });
 * // Returns numerical score based on weighted criteria
 * ```
 */
export const calculateBidScore = (scorecard: BidScorecardModel) => {
  const sum = (a: number, b: number) => a + b

  return BidScoreSchema.parse(
    Math.min(
      Math.max(
        [
          // Rating Categories
          toRatingCategoryScore(scorecard.experienceRating),
          toRatingCategoryScore(scorecard.qualityRating),
          // Count Categories
          toCountCategoryScore(scorecard.projectsRated),
          toCountCategoryScore(scorecard.projectsCompleted),
          // High Value Categories
          toHighValueCategoryScore(scorecard.inBudget),
          toHighValueCategoryScore(scorecard.inLocation),
          toHighValueCategoryScore(scorecard.tier1Status),
          toHighValueCategoryScore(scorecard.generalQfCertified),
          toHighValueCategoryScore(scorecard.mntnCertified),
          toHighValueCategoryScore(scorecard.tikTokCertified),
          toHighValueCategoryScore(scorecard.metaCertified),
          toHighValueCategoryScore(scorecard.meetsSkillTags),
          toHighValueCategoryScore(scorecard.meetsLanguageTags),
          // Medium Value Categories
          toMediumValueCategoryScore(scorecard.meetsPlatformTags),
          toMediumValueCategoryScore(scorecard.relevantExamples),
          // Low Value Categories
          toLowValueCategoryScore(scorecard.meetsIndustryTags),
          toLowValueCategoryScore(scorecard.similarPastBudget),
        ].reduce(sum, 0),
        0
      ),
      MaxBidScore
    )
  )
}
